import { observer } from 'mobx-react-lite';
import React, { Fragment, useContext, useEffect, useState } from 'react';
import { Field, Form as FinalForm } from 'react-final-form';
import { Button, Form, Icon, Segment } from 'semantic-ui-react';

import DateInput from '../../../app/common/form/DateInput';
import SelectInput from '../../../app/common/form/SelectInput';
import TextInput from '../../../app/common/form/TextInput';
import { formatParameterDisp } from '../../../app/common/util/util';
import { DepositSalesTicketFiltersFormValues } from '../../../app/models/depositSalesTicket';
import { IOption } from '../../../app/models/option';
import { RootStoreContext } from '../../../app/stores/rootStore';

const DepositSalesTicketFilters: React.FC = () => {
  const rootStore = useContext(RootStoreContext);
  const { agentFilterOptions } = rootStore.agentStore;
  const {
    mdTdTicketTypeFilterOptions,
    ticketReserveStatusFilterOptions,
    ticketReserveTypeFilterOptions,
    depositSalesTicketFilters,
    setDepositSalesTicketFilters,
  } = rootStore.depositSalesTicketStore;

  const [formValues, setFormValues] = useState(
    new DepositSalesTicketFiltersFormValues()
  );
  const [
    registrationDateFromSelectedValue,
    setRegistrationDateFromSelectedValue,
  ] = useState(null);
  const [startDateSelectedValue, setStartDateSelectedValue] = useState(null);
  const [
    ticketReserveTimeFromSelectedValue,
    setTicketReserveTimeFromSelectedValue,
  ] = useState(null);

  useEffect(() => {
    if (depositSalesTicketFilters !== null) {
      const { ...filters } = depositSalesTicketFilters;
      filters.registrationDateFrom =
        filters.registrationDateFrom && new Date(filters.registrationDateFrom);
      filters.registrationDateTo =
        filters.registrationDateTo && new Date(filters.registrationDateTo);
      filters.ticketReserveTimeFrom =
        filters.ticketReserveTimeFrom &&
        new Date(filters.ticketReserveTimeFrom);
      filters.ticketReserveTimeTo =
        filters.ticketReserveTimeTo && new Date(filters.ticketReserveTimeTo);
      filters.startDate = filters.startDate && new Date(filters.startDate);
      filters.limitMonthDate =
        filters.limitMonthDate && new Date(filters.limitMonthDate);
      setFormValues(new DepositSalesTicketFiltersFormValues(filters));
    }
  }, [depositSalesTicketFilters]);

  const handleFinalFormSubmit = (values: any) => {
    const { ...filters } = values;
    filters.registrationDateFrom =
      filters.registrationDateFrom &&
      formatParameterDisp(filters.registrationDateFrom);
    filters.registrationDateTo =
      filters.registrationDateTo &&
      formatParameterDisp(filters.registrationDateTo);
    filters.ticketReserveTimeFrom =
      filters.ticketReserveTimeFrom &&
      formatParameterDisp(filters.ticketReserveTimeFrom);
    filters.ticketReserveTimeTo =
      filters.ticketReserveTimeTo &&
      formatParameterDisp(filters.ticketReserveTimeTo);
    filters.startDate =
      filters.startDate && formatParameterDisp(filters.startDate);
    filters.limitMonthDate =
      filters.limitMonthDate && formatParameterDisp(filters.limitMonthDate);
    filters.agentId = filters.agentId !== '' ? filters.agentId : undefined;
    filters.mdTdTicketType =
      filters.mdTdTicketType !== '' ? filters.mdTdTicketType : undefined;
    filters.ticketReserveStatus =
      filters.ticketReserveStatus !== ''
        ? filters.ticketReserveStatus
        : undefined;
    filters.ticketReserveType =
      filters.ticketReserveType !== '' ? filters.ticketReserveType : undefined;
    setDepositSalesTicketFilters(filters);
  };

  return (
    <Fragment>
      <Segment placeholder>
        <FinalForm
          initialValues={formValues}
          onSubmit={handleFinalFormSubmit}
          mutators={{
            handleAgentChange: ([, data], state, utils) => {
              const { key } = data.options.find(
                (o: IOption) => o.value === data.value
              );
              utils.changeValue(state, 'agentId', () => key);
            },
            handleTenantChange: ([, data], state, { changeValue }) => {
              const { key } = data.options.find(
                (o: IOption) => o.value === data.value
              );
              changeValue(state, 'mdTdTicketType', () => key);
            },
            handleReserveStatusChange: ([, data], state, { changeValue }) => {
              const { key } = data.options.find(
                (o: IOption) => o.value === data.value
              );
              changeValue(state, 'ticketReserveStatus', () => key);
            },
            handleReserveTypeChange: ([, data], state, { changeValue }) => {
              const { key } = data.options.find(
                (o: IOption) => o.value === data.value
              );
              changeValue(state, 'ticketReserveType', () => key);
            },
            handleRegistrationDateFromChange: ([event]) => {
              setRegistrationDateFromSelectedValue(event);
            },
            handleStartDateChange: ([event]) => {
              setStartDateSelectedValue(event);
            },
            handleTicketReserveTimeFromChange: ([event]) => {
              setTicketReserveTimeFromSelectedValue(event);
            },
          }}
          render={({ form, handleSubmit }) => (
            <Form size='small' onSubmit={handleSubmit}>
              <Form.Group widths='16'>
                <Field
                  name='depositStartDate'
                  label='チケットNo（日付）'
                  placeholder='例：20200501'
                  maxLength={8}
                  value={formValues.depositStartDate}
                  component={TextInput}
                />
                <label>-</label>
                <Field
                  name='depositSeqNo'
                  label='（連番）'
                  placeholder='例：00001'
                  maxLength={5}
                  value={formValues.depositSeqNo}
                  component={TextInput}
                />
                <Field
                  name='agentName'
                  label='委託先'
                  options={agentFilterOptions || ''}
                  value={formValues.agentName}
                  component={SelectInput}
                  inputOnChange={form.mutators.handleAgentChange}
                />
                <Field
                  name='registrationDateFrom'
                  label='作成日（From）'
                  value={formValues.registrationDateFrom}
                  component={DateInput}
                  inputOnChange={form.mutators.handleRegistrationDateFromChange}
                />
                <label>～</label>
                <Field
                  name='registrationDateTo'
                  label='（To）'
                  value={formValues.registrationDateTo}
                  component={DateInput}
                  minDate={registrationDateFromSelectedValue!}
                />
                <Field
                  name='ticketReserveTimeFrom'
                  label='発券日（From）'
                  value={formValues.ticketReserveTimeFrom}
                  component={DateInput}
                  inputOnChange={
                    form.mutators.handleTicketReserveTimeFromChange
                  }
                />
                <label>～</label>
                <Field
                  name='ticketReserveTimeTo'
                  label='（To）'
                  value={formValues.ticketReserveTimeTo}
                  component={DateInput}
                  minDate={ticketReserveTimeFromSelectedValue!}
                />
              </Form.Group>
              <Form.Group widths='16'>
                <Field
                  name='startDate'
                  label='入場可能期間（From）'
                  value={formValues.startDate}
                  component={DateInput}
                  inputOnChange={form.mutators.handleStartDateFromChange}
                />
                <label>～</label>
                <Field
                  name='limitMonthDate'
                  label='（To）'
                  value={formValues.limitMonthDate}
                  component={DateInput}
                  minDate={startDateSelectedValue}
                />
                <Field
                  name='mdTdTicketTypeName'
                  label='テナント'
                  options={mdTdTicketTypeFilterOptions || ''}
                  value={formValues.mdTdTicketTypeName}
                  component={SelectInput}
                  inputOnChange={form.mutators.handleTenantChange}
                />
                <Field
                  name='ticketReserveTypeName'
                  label='発券区分'
                  options={ticketReserveTypeFilterOptions || ''}
                  value={formValues.ticketReserveTypeName}
                  component={SelectInput}
                  inputOnChange={form.mutators.handleReserveTypeChange}
                />
                <Field
                  name='ticketReserveStatusName'
                  label='ステータス'
                  options={ticketReserveStatusFilterOptions || ''}
                  value={formValues.ticketReserveStatusName}
                  component={SelectInput}
                  inputOnChange={form.mutators.handleReserveStatusChange}
                />
              </Form.Group>
              <Button
                style={{ marginTop: '25px' }}
                primary
                fluid
                type='submit'
                icon
                labelPosition='right'
              >
                検索
                <Icon name='search' />
              </Button>
            </Form>
          )}
        />
      </Segment>
    </Fragment>
  );
};

export default observer(DepositSalesTicketFilters);
